import { apiFetch, ApiNetworkError } from './apiClient';

// #878: DECIMER inference alone takes ~12s on an ordinary image, and the backend allows it up to
// 35s (app.ocsr.overall-timeout-ms), so apiFetch's 20s default would abort a working request.
const OCSR_TIMEOUT_MS = 45_000;

/**
 * #878: why recognition failed, as a stable kind the UI words for itself. The backend's
 * problem `detail` is developer-facing (it once leaked "content type [application/octet-stream]"
 * to users), so it's deliberately never shown.
 */
export type OcsrFailureKind =
  'timeout' | 'unavailable' | 'notRecognized' | 'rateLimited' | 'sessionExpired' | 'notAllowed' | 'unknown';

export class OcsrRecognitionError extends Error {
  constructor(
    readonly kind: OcsrFailureKind,
    message: string
  ) {
    super(message);
    this.name = 'OcsrRecognitionError';
  }
}

async function failureKind(response: Response): Promise<OcsrFailureKind> {
  if (response.status === 401) return 'sessionExpired';
  // #1064: the organisation doesn't allow outside lookups (the picture would go to DECIMER).
  if (response.status === 403) return 'notAllowed';
  if (response.status === 429) return 'rateLimited';
  if (response.status === 422) return 'notRecognized';
  if (response.status >= 500) {
    const body = (await response.json().catch(() => undefined)) as { reason?: unknown } | undefined;
    return body?.reason === 'TIMEOUT' ? 'timeout' : 'unavailable';
  }
  return 'unknown';
}

// On-demand only, mirroring lookupPubChemSmiles -- resolves an uploaded structure-depiction
// image to a SMILES string that the caller feeds into the existing SMILES-import pipeline, per
// #136's decision record. The request body is FormData, not JSON: no Content-Type header is set
// here so the browser can attach its own multipart boundary.
export async function recognizeStructureFromImage(file: File, handDrawn: boolean): Promise<string> {
  const formData = new FormData();
  formData.set('file', file);
  const path = `/external/ocsr?${new URLSearchParams({ handDrawn: String(handDrawn) })}`;
  let response: Response;
  try {
    response = await apiFetch(path, { method: 'POST', body: formData }, OCSR_TIMEOUT_MS);
  } catch (error) {
    const timedOut = error instanceof ApiNetworkError && error.timedOut;
    throw new OcsrRecognitionError(timedOut ? 'timeout' : 'unavailable', error instanceof Error ? error.message : '');
  }
  if (!response.ok) {
    throw new OcsrRecognitionError(await failureKind(response), `OCSR request failed with status ${response.status}.`);
  }
  const body = (await response.json()) as { smiles: string };
  return body.smiles;
}
